import { useEffect, useState } from "react";
import { useAuth } from 'auth/Auth';
import { useNavigate } from 'react-router-dom';
import React, { useMemo } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import {
    Autocomplete,
    Box,
    Button,
    Dialog,
    Chip,
    Tooltip,
    Divider,
    IconButton,
    TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import colors from "assets/theme/base/colors";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import StatusBadge from "components/StatusBadge";
import MonoText from "components/MonoText";
import TableSkeleton from "components/CustomSkeleton/TableSkeleton";
import TableActionMenu from 'components/TableActionMenu';
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

const TripPackages = () => {
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();

    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, tripId: null, packageId: null });

    // --- Add Package dialog state ---
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [trips, setTrips] = useState([]);
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [loadingTrips, setLoadingTrips] = useState(false);

    useEffect(() => {
        fetchPackages();
    }, []);

    useEffect(() => {
        if (!addDialogOpen) {
            setSelectedTrip(null);
            return;
        }

        const fetchTrips = async () => {
            setLoadingTrips(true);
            try {
                const response = await axiosInstance.get("/trips");
                setTrips(response.data || []);
            } catch (error) {
                console.error("Error fetching trips:", error);
                setTrips([]);
            } finally {
                setLoadingTrips(false);
            }
        };

        fetchTrips();
    }, [addDialogOpen, axiosInstance]);

    const fetchPackages = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/trip-packages');
            const sorted = [...(response.data || [])].sort((a, b) => {
                const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
                const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
                return bTime - aTime;
            });
            setPackages(sorted);
        } catch (error) {
            console.error('Error fetching trip packages:', error);
            setSnackbar({ open: true, message: 'Error loading trip packages', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // No standalone package detail page (yet) — reuse the trip's detail view.
    const handleRowClick = (rowData) => {
        navigate(`/trip-packages/detail/${rowData.id}`);
    };

    const getFirstTripId = (pkg) => pkg?.trips?.[0]?.id;

    const handleEditClick = (rowData) => {
        const tripId = getFirstTripId(rowData);
        if (!tripId) {
            setSnackbar({ open: true, message: 'This package has no associated trip', severity: 'error' });
            return;
        }
        navigate(`/trips/${tripId}/packages/${rowData.id}/edit?step=packages`);
    };

    const handleDeleteClick = (packageId) => {
        const pkg = packages.find((p) => p.id === packageId);
        if (!pkg) return;
        setDeleteConfirm({ open: true, tripId: pkg.trip_id, packageId: pkg.id });
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/packages/${deleteConfirm.packageId}`);
            setSnackbar({ open: true, message: 'Package deleted successfully', severity: 'success' });
            fetchPackages();
        } catch (error) {
            console.error('Error deleting package:', error);
            setSnackbar({ open: true, message: 'Error deleting package', severity: 'error' });
            throw error;
        }
    };

    const handleCloseDeleteModal = () => setDeleteConfirm({ open: false, tripId: null, packageId: null });

    // --- Add Package dialog handlers ---

    const closeAddDialog = () => setAddDialogOpen(false);

    const handleProceed = () => {
        if (!selectedTrip?.id) return;
        navigate(`/trips/${selectedTrip.id}/edit?step=packages`);
        closeAddDialog();
    };

    const tableData = useMemo(() => ({
        columns: [
            {
                Header: "Package",
                accessor: "title",
                width: "18%",
                Cell: ({ row }) => (
                    <MDTypography
                        component="span"
                        onClick={() => handleRowClick(row.original)}
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{
                            cursor: "pointer",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {row.original.title || "Untitled Package"}
                    </MDTypography>
                ),
            },
            {
                Header: "Country",
                accessor: "trips",
                width: "16%",
                Cell: ({ row }) => {
                    const countries = [
                        ...new Set(
                            (row.original.trips || [])
                            .map((trip) => trip.primary_country)
                            .filter(Boolean)
                        ),
                    ];

                    if (countries.length === 0) return "-";

                    const [first, ...rest] = countries;

                    return (
                        <MDBox display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                            <Chip
                                label={first}
                                size="small"
                                variant="outlined"
                                title={first}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: "0.7rem",
                                    backgroundColor: "divider",
                                    height: "auto",
                                    "& .MuiChip-label": {
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        display: "block",
                                        py: 0.5,
                                    },
                                }}
                            />
                            {rest.length > 0 && (
                            <Tooltip title={rest.join(", ")} arrow placement="top">
                                <Chip
                                    label={`+${rest.length}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: 600, fontSize: "0.7rem", cursor: "default", backgroundColor: "divider" }}
                                />
                            </Tooltip>
                            )}
                            </MDBox>
                        );
                    },
            },
            {
                Header: "Status",
                accessor: "status",
                width: "14%",
                Cell: ({ value }) => <StatusBadge status={value || "draft"} />,
            },
            {
                Header: "Duration",
                accessor: "duration_days",
                width: "10%",
                Cell: ({ value }) => (value ? `${value}d` : "-"),
            },
            {
                Header: "Modified",
                accessor: "updated_at",
                width: "12%",
                Cell: ({ value }) => (
                    <MonoText>
                        {value ? new Date(value).toLocaleDateString() : "-"}
                    </MonoText>
                ),
            },
            {
                Header: "Actions",
                accessor: "actions",
                width: "8%",
                align: "center",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <TableActionMenu
                        row={row.original}
                        onView={handleRowClick}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                    />
                ),
            },
        ],
        rows: packages,
    }), [packages]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h6" color="white">
                                        Trip Packages
                                    </MDTypography>
                                    <MDTypography variant="caption" color="white" opacity={0.86}>
                                        Manage packages across all trips.
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="dark" onClick={() => setAddDialogOpen(true)}>
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Create New Package
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : (
                                    <DataTable
                                        table={tableData}
                                        isSorted
                                        noEndBorder
                                        canSearch
                                        exportCSV
                                        entriesPerPage={{
                                            defaultValue: 10,
                                            entries: [5, 10, 15, 20, 25],
                                        }}
                                        showTotalEntries
                                        pagination={{ variant: "gradient", color: "info" }}
                                        objectType="trip package"
                                        onFilterClear={fetchPackages}
                                    />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ConfirmDeleteModal
                open={deleteConfirm.open}
                onClose={handleCloseDeleteModal}
                onConfirm={handleDelete}
                title="Delete Package"
                message="Are you sure you want to delete this package? This action cannot be undone."
            />

            {/* --- Add Package dialog --- */}
            <Dialog
                open={addDialogOpen}
                onClose={closeAddDialog}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: "100%",
                        maxWidth: 460,
                        m: 2,
                        borderRadius: "16px",
                        boxShadow: "0 20px 48px rgba(14, 35, 72, 0.16)",
                        overflow: "hidden",
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2.5,
                        pt: 1.75,
                        pb: 1.5,
                    }}
                >
                    <Box
                        component="span"
                        sx={{
                            fontFamily: '"Sora", "Roboto", sans-serif',
                            fontSize: "1.1rem",
                            fontWeight: 700,
                            color: colors.dark.main,
                            lineHeight: 1.3,
                        }}
                    >
                        Add Package
                    </Box>
                    <IconButton
                        size="small"
                        onClick={closeAddDialog}
                        aria-label="Close"
                        sx={{
                            width: 30,
                            height: 30,
                            border: `1px solid ${colors.grey[400]}`,
                            borderRadius: "8px",
                            color: colors.text.main,
                            "&:hover": { bgcolor: colors.grey[200] },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>

                <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

                {/* Body */}
                <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
                    <MDTypography
                        variant="body2"
                        sx={{ color: colors.primary.main, fontWeight: 500, mb: 2 }}
                    >
                        Select an existing destination to configure new Packages.
                    </MDTypography>

                    <MDTypography
                        variant="caption"
                        sx={{ color: colors.text.main, fontWeight: 600, display: 'block', mb: 0.75 }}
                    >
                        Select Destination <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                    </MDTypography>

                    <Autocomplete
                        options={trips}
                        loading={loadingTrips}
                        value={selectedTrip}
                        onChange={(_, value) => setSelectedTrip(value)}
                        getOptionLabel={(option) =>
                            option.title
                            || `${option.primary_country || 'Untitled Trip'}${option.category ? ` (${option.category})` : ''}`
                            // || `${option.primary_country || 'Untitled Trip'}${option.category ? ` - ${option.category}` : ''} (#${option.id})`
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search or select destination..."
                                size="small"
                                fullWidth
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "11px",
                                        fontSize: "0.9rem",
                                        "&:hover fieldset": { borderColor: colors.primary.main },
                                        "&.Mui-focused fieldset": {
                                            borderColor: colors.primary.main,
                                            boxShadow: `0 0 0 3px ${colors.brand.primaryContainer}`,
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                <Divider sx={{ borderColor: colors.grey[300], m: 0 }} />

                {/* Footer */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 1,
                        px: 2.5,
                        pt: 1.25,
                        pb: 1.5,
                    }}
                >
                    <Button
                        onClick={closeAddDialog}
                        sx={{
                            minWidth: 92,
                            py: 0.85,
                            px: 2,
                            borderRadius: "999px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.84rem",
                            color: colors.text.main,
                            border: `1px solid ${colors.grey[400]}`,
                            bgcolor: colors.white.main,
                            "&:hover": {
                                bgcolor: colors.grey[200],
                                borderColor: colors.grey[500],
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProceed}
                        disabled={!selectedTrip}
                        sx={{
                            minWidth: 92,
                            py: 0.85,
                            px: 2.5,
                            borderRadius: "999px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "0.84rem",
                            color: colors.white.main,
                            background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.dark.focus} 100%)`,
                            boxShadow: "0 6px 16px rgba(21, 101, 192, 0.28)",
                            "&:hover": {
                                color: colors.white.main,
                                background: `linear-gradient(135deg, ${colors.primary.focus} 0%, ${colors.primary.main} 100%)`,
                                boxShadow: "0 8px 20px rgba(21, 101, 192, 0.32)",
                            },
                            "&:disabled": {
                                color: colors.grey[600],
                                background: colors.grey[300],
                                boxShadow: "none",
                            },
                        }}
                    >
                        Proceed
                    </Button>
                </Box>
            </Dialog>
        </DashboardLayout>
    );
};

export default TripPackages;